<template>
  <v-dialog v-model="dialog" max-width="500px" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-alert-circle" color="warning" class="mr-3"></v-icon>
        <span>Reportar Incidencia</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <!-- Tipo de incidencia -->
          <v-select
            v-model="incidentData.type"
            :items="incidentTypes"
            label="Tipo de Incidencia"
            required
            :rules="[rules.required]"
            class="mb-3"
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props" :title="item.title">
                <template #prepend>
                  <v-icon :icon="item.raw.icon" :color="item.raw.color"></v-icon>
                </template>
                <template #subtitle>
                  <span class="text-caption">{{ item.raw.description }}</span>
                </template>
              </v-list-item>
            </template>
          </v-select>

          <!-- Prioridad -->
          <v-select
            v-model="incidentData.priority"
            :items="priorityLevels"
            label="Prioridad"
            required
            :rules="[rules.required]"
            class="mb-3"
          >
            <template #selection="{ item }">
              <v-chip :color="item.raw.color" size="small">
                <v-icon start size="small" :icon="item.raw.icon"></v-icon>
                {{ item.title }}
              </v-chip>
            </template>
          </v-select>

          <!-- Agencia afectada (opcional) -->
          <v-autocomplete
            v-model="incidentData.agencyId"
            :items="nearbyAgencies"
            item-title="name"
            item-value="id"
            label="Agencia Afectada (Opcional)"
            clearable
            class="mb-3"
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props" :title="item.raw.name">
                <template #subtitle>
                  <span class="text-caption">{{ item.raw.address }}</span>
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>

          <!-- Descripción -->
          <v-textarea
            v-model="incidentData.description"
            label="Descripción de la Incidencia"
            placeholder="Describe detalladamente lo que está ocurriendo..."
            required
            :rules="[rules.required, rules.minLength]"
            rows="4"
            class="mb-3"
          ></v-textarea>

          <!-- Ubicación automática -->
          <v-alert v-if="currentLocation" color="info" variant="tonal" class="mb-3">
            <v-icon icon="mdi-map-marker" start></v-icon>
            <strong>Ubicación:</strong> {{ currentLocation }}
          </v-alert>

          <!-- Captura de foto (opcional) -->
          <div class="mb-3">
            <v-btn
              @click="capturePhoto"
              color="primary"
              variant="outlined"
              :disabled="!canUseCamera"
              class="mr-2"
            >
              <v-icon start>mdi-camera</v-icon>
              Tomar Foto
            </v-btn>

            <v-btn
              @click="selectFile"
              color="secondary"
              variant="outlined"
            >
              <v-icon start>mdi-file-image</v-icon>
              Seleccionar Archivo
            </v-btn>

            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="onFileSelected"
            />
          </div>

          <!-- Preview de imagen -->
          <v-card v-if="imagePreview" class="mb-3" elevation="2">
            <v-img
              :src="imagePreview"
              height="200"
              cover
            ></v-img>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                @click="removeImage"
                color="error"
                variant="text"
                size="small"
              >
                <v-icon start size="small">mdi-delete</v-icon>
                Eliminar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          @click="closeDialog"
          color="grey"
          variant="text"
        >
          Cancelar
        </v-btn>
        <v-btn
          @click="submitIncident"
          color="warning"
          :disabled="!valid"
          :loading="submitting"
        >
          <v-icon start>mdi-send</v-icon>
          Reportar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

interface Props {
  modelValue: boolean
  currentLocation?: string
  nearbyAgencies?: Array<{
    id: number
    name: string
    address: string
  }>
}

const props = withDefaults(defineProps<Props>(), {
  nearbyAgencies: () => []
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'incident-reported': [incidentData: any]
}>()

// Estado reactivo
const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const form = ref()
const valid = ref(false)
const submitting = ref(false)
const fileInput = ref<HTMLInputElement>()
const imagePreview = ref<string>('')

const incidentData = ref({
  type: '',
  priority: '',
  agencyId: null,
  description: '',
  image: null as File | null
})

// Datos de configuración
const incidentTypes = [
  {
    title: 'Personal',
    value: 'personal',
    icon: 'mdi-account-alert',
    color: 'red',
    description: 'Sin empleada, tardanzas, ausentismo'
  },
  {
    title: 'Tecnológica',
    value: 'technology',
    icon: 'mdi-wifi-off',
    color: 'orange',
    description: 'Internet, equipos, sistemas eléctricos'
  },
  {
    title: 'Infraestructura',
    value: 'infrastructure',
    icon: 'mdi-wrench',
    color: 'blue',
    description: 'Puertas, accesos, mantenimiento'
  },
  {
    title: 'Operacional',
    value: 'operational',
    icon: 'mdi-package-variant',
    color: 'purple',
    description: 'Inventario, suministros, logística'
  },
  {
    title: 'Seguridad',
    value: 'security',
    icon: 'mdi-shield-alert',
    color: 'red-darken-2',
    description: 'Problemas de seguridad o acceso'
  },
  {
    title: 'Otro',
    value: 'other',
    icon: 'mdi-help-circle',
    color: 'grey',
    description: 'Otro tipo de incidencia'
  }
]

const priorityLevels = [
  {
    title: 'Crítica',
    value: 'critical',
    icon: 'mdi-alert',
    color: 'red',
    description: 'Requiere atención inmediata'
  },
  {
    title: 'Alta',
    value: 'high',
    icon: 'mdi-alert-circle',
    color: 'orange',
    description: 'Importante, resolver pronto'
  },
  {
    title: 'Media',
    value: 'medium',
    icon: 'mdi-information',
    color: 'yellow-darken-2',
    description: 'Resolver en horario normal'
  },
  {
    title: 'Baja',
    value: 'low',
    icon: 'mdi-minus-circle',
    color: 'green',
    description: 'No urgente'
  }
]

// Reglas de validación
const rules = {
  required: (v: any) => !!v || 'Este campo es requerido',
  minLength: (v: string) => (v && v.length >= 10) || 'Mínimo 10 caracteres'
}

// Capacidades del dispositivo
const canUseCamera = computed(() => {
  return navigator.mediaDevices && navigator.mediaDevices.getUserMedia
})

// Métodos
const closeDialog = () => {
  resetForm()
  dialog.value = false
}

const resetForm = () => {
  incidentData.value = {
    type: '',
    priority: '',
    agencyId: null,
    description: '',
    image: null
  }
  imagePreview.value = ''
  valid.value = false
}

const submitIncident = async () => {
  if (!valid.value) return

  submitting.value = true
  try {
    const submitData = {
      ...incidentData.value,
      timestamp: new Date().toISOString(),
      location: props.currentLocation,
      image: incidentData.value.image
    }

    emit('incident-reported', submitData)
    closeDialog()
  } catch (error) {
    console.error('Error al reportar incidencia:', error)
  } finally {
    submitting.value = false
  }
}

const capturePhoto = async () => {
  if (!canUseCamera.value) return

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ 
      video: { facingMode: 'environment' } 
    })
    
    // Crear elemento de video temporal para capturar
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    video.srcObject = stream
    video.play()

    video.addEventListener('loadedmetadata', () => {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      
      // Capturar frame después de un segundo
      setTimeout(() => {
        context?.drawImage(video, 0, 0)
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'incident-photo.jpg', { type: 'image/jpeg' })
            incidentData.value.image = file
            imagePreview.value = URL.createObjectURL(blob)
          }
        }, 'image/jpeg', 0.8)
        
        // Detener stream
        stream.getTracks().forEach(track => track.stop())
      }, 1000)
    })
  } catch (error) {
    console.error('Error al acceder a la cámara:', error)
  }
}

const selectFile = () => {
  fileInput.value?.click()
}

const onFileSelected = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (file && file.type.startsWith('image/')) {
    incidentData.value.image = file
    imagePreview.value = URL.createObjectURL(file)
  }
}

const removeImage = () => {
  incidentData.value.image = null
  imagePreview.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Limpiar form cuando se cierra
watch(dialog, (newVal) => {
  if (!newVal) {
    setTimeout(resetForm, 300)
  }
})
</script>

<style scoped>
:deep(.v-textarea .v-field__input) {
  padding-top: 12px;
}

:deep(.v-alert) {
  font-size: 0.875rem;
}

.v-img {
  border-radius: 8px;
}
</style>
